/**
 * StudioPaidMediaAnalytics — iter244
 *
 * Aggregierte KPIs für den Creator im Studio → Paid-Media-Tab:
 *   · Umsatz (revenue_eur, brutto vor Split)
 *   · Kauf-Zähler pro Asset
 *   · Erstkauf-Datum (first_purchase_at)
 *   · Download-Ratio delivered_count / purchase_count
 *   · Fingerprint-/Takedown-Alerts der letzten 30 Tage
 *
 * Backend: GET /api/studio/paid-media/analytics
 */
import { useEffect, useState } from "react";
import { Wallet, ShoppingCart, Download, ShieldAlert, Loader2, TrendingUp, Lock, CalendarDays } from "lucide-react";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";

function fmtEur(v) {
  return `${Number(v || 0).toFixed(2)} €`;
}

function fmtDate(iso) {
  if (!iso) return "–";
  try {
    return new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch { return "–"; }
}

function Ratio({ value }) {
  const pct = Math.round((value || 0) * 100);
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 rounded-full bg-[#F0F0F0] overflow-hidden">
        <div className="h-full bg-[#0A0A0A] transition-all"
              style={{ width: `${Math.min(100, pct)}%` }} />
      </div>
      <span className="text-[10px] text-[#666] tabular-nums">{pct}%</span>
    </div>
  );
}

function TotalTile({ icon: Icon, label, value, sub, testId, warn }) {
  return (
    <div data-testid={testId} className={`p-3 rounded-2xl border ${warn ? "border-amber-300 bg-amber-50" : "border-[#E5E5E5] bg-white"}`}>
      <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wider text-[#666]">
        <Icon size={12} /> {label}
      </div>
      <div className="text-lg font-bold text-[#0A0A0A] mt-1 tabular-nums">{value}</div>
      {sub && <div className="text-[10px] text-[#999] mt-0.5">{sub}</div>}
    </div>
  );
}

export default function StudioPaidMediaAnalytics() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setErr("");
      try {
        const { data: rsp } = await api.get("/studio/paid-media/analytics");
        if (!cancelled) setData(rsp);
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || "Fehler beim Laden");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div data-testid="studio-analytics-loading" className="flex items-center justify-center py-8 text-[#666]">
        <Loader2 size={16} className="animate-spin" />
      </div>
    );
  }
  if (err) {
    return <div data-testid="studio-analytics-error" className="text-xs text-red-600 py-3">{typeof err === "string" ? err : JSON.stringify(err)}</div>;
  }
  const items = data?.items || [];
  const totals = data?.totals || {};
  if (items.length === 0) {
    return null; // Nichts anzuzeigen ohne Paid-Media
  }
  return (
    <section data-testid="studio-paid-analytics" className="space-y-3 mb-4">
      <p className="text-xs font-semibold uppercase tracking-wider text-[#666]">
        {t("studio.paid_analytics_title", { defaultValue: "Paid-Media Analytics" })}
      </p>
      {/* Gesamt-Kacheln */}
      <div className="grid gap-2" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))" }}>
        <TotalTile
          icon={Wallet}
          label={t("studio.analytics_revenue", { defaultValue: "Umsatz gesamt" })}
          value={fmtEur(totals.revenue_eur)}
          sub={t("studio.analytics_gross", { defaultValue: "vor Split" })}
          testId="studio-analytics-revenue"
        />
        <TotalTile
          icon={ShoppingCart}
          label={t("studio.analytics_purchases", { defaultValue: "Käufe" })}
          value={totals.purchase_count || 0}
          sub={t("studio.analytics_paid_content", {
            count: totals.asset_count || 0,
            defaultValue: "{{count}} Beiträge",
          })}
          testId="studio-analytics-purchases"
        />
        <TotalTile
          icon={Download}
          label={t("studio.analytics_downloads", { defaultValue: "Downloads" })}
          value={`${totals.delivered_count || 0} / ${totals.purchase_count || 0}`}
          sub={`Ø ${Math.round((totals.average_download_ratio || 0) * 100)}%`}
          testId="studio-analytics-downloads"
        />
        <TotalTile
          icon={ShieldAlert}
          label={t("studio.analytics_alerts", { defaultValue: "Alerts (30T)" })}
          value={totals.fingerprint_alerts || 0}
          sub={t("studio.analytics_alerts_sub", { defaultValue: "Takedown/Fingerprint" })}
          warn={(totals.fingerprint_alerts || 0) > 0}
          testId="studio-analytics-alerts"
        />
      </div>
      {/* Detail-Tabelle */}
      <details className="rounded-2xl border border-[#E5E5E5] bg-white" data-testid="studio-analytics-details">
        <summary className="cursor-pointer list-none px-3 py-2 flex items-center justify-between text-xs text-[#0A0A0A] font-medium hover:bg-[#FAFAFA]">
          <span className="flex items-center gap-1"><TrendingUp size={12} /> {t("studio.analytics_per_asset", { defaultValue: "Pro Beitrag" })}</span>
          <span className="text-[#999]">{items.length}</span>
        </summary>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-t border-[#E5E5E5] text-left text-[10px] uppercase text-[#999]">
                <th className="px-3 py-2 font-semibold">Titel</th>
                <th className="px-3 py-2 font-semibold text-right">Umsatz</th>
                <th className="px-3 py-2 font-semibold text-right">{t("paid_media.purchases")}</th>
                <th className="px-3 py-2 font-semibold">Erstkauf</th>
                <th className="px-3 py-2 font-semibold min-w-[100px]">DL-Ratio</th>
                <th className="px-3 py-2 font-semibold text-right">Alerts</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.asset_id}
                    data-testid={`studio-analytics-row-${it.asset_id}`}
                    className="border-t border-[#F0F0F0] hover:bg-[#FAFAFA]">
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1.5">
                      {it.lifecycle_state === "LOCKED_AFTER_FIRST_PURCHASE" && (
                        <Lock size={10} className="text-[#0A0A0A] shrink-0" title="Gesperrt seit Erstkauf" />
                      )}
                      <span className="truncate max-w-[220px]">{it.title || it.asset_id.slice(0, 8)}</span>
                    </div>
                    <span className="text-[10px] text-[#999]">{it.media_type} · {fmtEur(it.price_eur)}</span>
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmtEur(it.revenue_eur)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{it.purchase_count}</td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <span className="inline-flex items-center gap-1 text-[10px] text-[#666]">
                      <CalendarDays size={10} /> {fmtDate(it.first_purchase_at)}
                    </span>
                  </td>
                  <td className="px-3 py-2 min-w-[100px]"><Ratio value={it.download_ratio} /></td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {it.fingerprint_alerts > 0 ? (
                      <span className="inline-flex items-center gap-1 text-amber-700">
                        <ShieldAlert size={10} /> {it.fingerprint_alerts}
                      </span>
                    ) : "–"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </section>
  );
}
